import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  HelpCircle, 
  Lock, 
  TrendingUp, 
  AlertTriangle, 
  ChevronDown, 
  Scale 
} from 'lucide-react';

export default function FAQ() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('general');
  const [openIndex, setOpenIndex] = useState(null);

  // Check if user came from Footer Policy Link
  useEffect(() => {
    if (location.state && location.state.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = {
    general: {
      category: "General & Services",
      icon: <HelpCircle className="w-4 h-4" />,
      questions: [
        {
          q: "What is Welvate and how does it operate?",
          a: "Welvate is a modern financial education and wealth management consulting firm founded by Haritika. We offer research-backed learning pathways, unbiased financial planning, and wealth distribution services across Mutual Funds, PMS, AIFs, and fixed-income assets without offering speculative stock tips."
        },
        {
          q: "Does Welvate provide guaranteed returns or intraday trading tips?",
          a: "No. Welvate operates strictly as an educational and research-driven wealth advisory platform. We do not provide intraday stock tips, quick-rich schemes, or guaranteed return promises. Wealth creation is built on risk discipline and long-term compounding."
        },
        {
          q: "How can I schedule a 1-on-1 consultation with Haritika?",
          a: "You can book a personalized 1-on-1 portfolio review and financial planning session through our 'Contact Us' page or by reaching out to our advisory team at hello@welvate.com."
        },
        {
          q: "Who holds my invested funds?",
          a: "Welvate never holds or pools client funds. All investments (Mutual Funds, SIPs, PMS, AIFs) are processed directly through registered Asset Management Companies (AMCs), exchange portals, and SEBI-regulated custodians."
        }
      ]
    },
    trading: {
      category: "Trading & Advisory Compliance",
      icon: <TrendingUp className="w-4 h-4" />,
      questions: [
        {
          q: "Is Welvate and its leadership certified?",
          a: "Yes. Our founder, Haritika, holds certifications under NISM (Series V-A & Series VIII) and IRDAI. All investment execution and advisory frameworks comply with standard AMFI, SEBI, and IRDAI regulatory guidelines."
        },
        {
          q: "How does Welvate distinguish between active trading and investing?",
          a: "Active trading (Equities/Derivatives) requires strict technical analysis, position sizing, and risk management. Long-term investing focuses on asset allocation and fundamental compounding. Welvate educates clients to understand both risk profiles before allocating capital."
        },
        {
          q: "Does Welvate offer advice on Pre-IPO and Unlisted Shares?",
          a: "Yes, we facilitate access to curated Pre-IPO and unlisted opportunities for eligible investors, complete with fundamental research, risk assessments, and liquidity timelines."
        }
      ]
    },
    privacy: {
      category: "Privacy & Data Protection",
      icon: <Lock className="w-4 h-4" />,
      questions: [
        {
          q: "Privacy Policy: How is my personal and financial data protected?",
          a: "We adhere to strict data protection standards. Your personal credentials, contact information, and financial profiles are encrypted and stored securely. We never sell, rent, or trade client data to third-party telemarketers."
        },
        {
          q: "What information do we collect from website users?",
          a: "We collect basic contact information (name, email, phone number, investment interest) provided voluntarily through our consultation forms to facilitate account setup, advisory communication, and course enrolment."
        },
        {
          q: "Can I request the deletion of my personal data?",
          a: "Yes. Clients can request complete deletion or modification of their account records by writing to our compliance officer at privacy@welvate.com."
        }
      ]
    },
    disclaimer: {
      category: "Legal, Risk & Compliance Policies",
      icon: <Scale className="w-4 h-4" />,
      questions: [
        {
          q: "1. Terms & Conditions (Website Usage)",
          a: "By accessing Welvate, users agree not to redistribute, copy, or commercialize proprietary course modules, research reports, or brand assets without explicit written consent from Welvate management."
        },
        {
          q: "2. Regulatory Market Risk Disclosure",
          a: "Mutual fund investments and equity market allocations are subject to market risks. Past performance does not guarantee future results. Please read all scheme-related documents and risk disclosures carefully before investing."
        },
        {
          q: "3. Grievance Redressal Policy",
          a: "We take client satisfaction seriously. For any unresolved complaints regarding course modules or advisory support, clients can submit a formal grievance ticket directly to grievance@welvate.com. Resolution timeline is within 5-7 working days."
        },
        {
          q: "4. Compliance & Regulatory Framework",
          a: "Welvate provides financial educational materials and wealth distribution services under applicable certifications (NISM Equity Derivatives, NISM Mutual Fund Distribution, IRDAI Insurance). We do not provide stock tips or guaranteed returns."
        },
        {
          q: "5. Cancellation & Refund Policy for Educational Modules",
          a: "Fees paid for digital learning courses and 1-on-1 advisory bookings are eligible for a partial or full refund if cancelled at least 24 hours prior to the session start time. Detailed terms apply as outlined in our Refund Policy document."
        }
      ]
    }
  };

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans selection:bg-[#16325B] selection:text-white py-12 px-6">
      <div className="max-w-5xl mx-auto space-y-12">
        
        {/* Page Hero Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] border border-[#C7D7E8] text-[#16325B] text-xs font-semibold px-4 py-1.5 rounded-full">
            <ShieldCheck className="w-4 h-4 text-[#16325B]" />
            Help Center & Policies
          </div>

          <h1 className="font-serif-title text-4xl sm:text-5xl font-extrabold text-[#0B2540] tracking-tight">
            Frequently Asked <span className="text-[#16325B] italic font-normal">Questions.</span>
          </h1>

          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Find answers regarding our wealth management services, trading compliance, privacy standards, and regulatory policies.
          </p>
        </div>

        {/* Category Selector Tabs */}
        <div className="flex flex-wrap justify-center gap-3">
          {Object.keys(faqData).map((key) => (
            <button
              key={key}
              onClick={() => {
                setActiveTab(key);
                setOpenIndex(null);
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold transition-all duration-300 border ${
                activeTab === key
                  ? 'bg-[#16325B] text-white border-[#16325B] shadow-md'
                  : 'bg-white text-gray-700 border-gray-200/80 hover:border-[#16325B] hover:text-[#16325B]'
              }`}
            >
              {faqData[key].icon}
              <span>{faqData[key].category}</span>
            </button>
          ))}
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {faqData[activeTab].questions.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden shadow-sm hover:border-[#16325B]/40 transition duration-300"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-6 text-left flex justify-between items-center gap-4 focus:outline-none"
              >
                <span className="font-serif-title font-bold text-base sm:text-lg text-[#0B2540]">
                  {faq.q}
                </span>
                <div className={`w-8 h-8 rounded-full bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180 bg-[#16325B] text-white' : ''
                }`}>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden border-t border-gray-100 bg-[#FAF8F5]/60"
                  >
                    <p className="p-6 text-xs sm:text-sm text-gray-600 leading-relaxed font-medium">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Regulatory & Risk Banner */}
        <div className="bg-[#EEF4FA] border border-[#C7D7E8] rounded-3xl p-6 flex flex-col sm:flex-row items-start gap-4 shadow-sm">
          <div className="w-10 h-10 rounded-2xl bg-[#16325B] text-white flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="space-y-1 text-xs text-gray-700">
            <strong className="text-[#0B2540] font-bold text-sm block">Important Legal Notice:</strong>
            <p className="leading-relaxed">
              Welvate provides financial educational materials and wealth distribution services. We do not offer guaranteed returns. Capital markets carry inherent risks, and past performance is no guarantee of future results. Please ensure you evaluate your risk appetite prior to committing funds.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}